<script>
import { getLocationDetails } from '@utils/contentful'
import { mapGetters } from 'vuex'
import Layout from '@layouts/main.vue'

export default {
  page: {
    title: 'Unavailable',
    meta: [{ name: 'description', content: 'The unavailable page.' }],
  },
  components: { Layout },
  data: () => ({
    loading: true,
    location: {},
  }),
  computed: {
    ...mapGetters('contentful', {
      brand: 'getBrand',
      content: 'getContent',
    }),
    ...mapGetters('location', {
      isWantToOrderPromptVisible: 'isWantToOrderPromptVisible',
    }),
    description() {
      return (
        this.location?.unavailableDescription ||
        'Please try checkout again by going to homepage or place an order. Alternatively talk to a server.'
      )
    },
    orderBaseUrl() {
      return this.brand?.oatBaseUrl || this.content?.oatBaseUrl
    },
    orderLink() {
      /**
       * if slug and table number are known, they can
       * be added to the base url
       */
      const { orderBaseUrl, showOrderLink } = this
      if (!orderBaseUrl || !showOrderLink) {
        return null
      }

      let orderUrl = orderBaseUrl

      const { slug, table } = this
      if (!slug) {
        return orderUrl
      }

      orderUrl = `${orderUrl}${slug}/order`

      if (table) {
        orderUrl = `${orderUrl}?table=${table}`
      }

      return orderUrl
    },
    orderLinkText() {
      return this.location?.oatLinkText || 'Order'
    },
    showOrderLink() {
      const { slug } = this
      const isEnabled = this.location?.showOatLink === 'Show'

      return slug && isEnabled
    },
    siteId() {
      return this.$route.query.location
    },
    slug() {
      return this.location?.slug
    },
    table() {
      return this.$route.query.table
    },
    title() {
      return this.location?.unavailableTitle || 'Something went wrong'
    },
  },
  async created() {
    /**
     * if siteId has been specified, get location/restaurant data
     * in order to determine if OAT link should be displayed
     * otherwise show generic unavailable page
     */
    try {
      const { siteId } = this

      if (siteId) {
        this.location = await getLocationDetails(siteId)
      }

      this.loading = false
    } catch (error) {
      this.loading = false
    }
  },
}
</script>

<template>
  <Layout :loading="loading" screen="failure" disable-header-link>
    <main :class="$style.page">
      <h3>
        {{ title }}
      </h3>
      <p :class="$style.description">{{ description }}</p>
      <BaseLink :to="{ name: 'home' }">
        <BaseButton v-text="'Homepage'" />
      </BaseLink>
    </main>

    <div
      v-if="isWantToOrderPromptVisible && orderLink && showOrderLink"
      class="footer"
      :class="$style.footer"
    >
      <h4 :class="$style.footerText">Want to order?</h4>
      <a :href="orderLink">
        <BaseButton v-text="orderLinkText" />
      </a>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.page {
  flex: 1;
  text-align: center;
  max-width: 280px;
  margin: auto;
  padding: 3rem 0;
}

.description {
  font-family: $content-font-family;
  font-weight: $content-font-weight;
}

.error {
  width: 70%;
  margin: 2rem auto;
}

.footer {
  border-top: 2px dashed var(--color-dark-grey);
  height: 8.4375rem;
  width: 100%;
}

.footerText {
  font-size: 1rem;
  margin-bottom: 1rem;
}
</style>
